// Small, dependency-free Excel (.xlsx) writer and reader, plus a CSV reader.
//
// Writing works anywhere (the browser builds the downloads): files are stored in the zip uncompressed, which every
// spreadsheet program reads. Reading needs a raw-deflate decompressor because files saved by Excel, LibreOffice and
// Google Sheets are compressed: pass one in (Node's zlib.inflateRawSync on the server).

/* ── ZIP ──────────────────────────────────────────────── */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(bytes) {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

const enc = new TextEncoder();
const dec = new TextDecoder('utf-8');

export function zip(files) {
  // files: [{ name, data: Uint8Array | string }]
  const parts = [];
  const central = [];
  let offset = 0;
  const now = new Date();
  const time = ((now.getHours() << 11) | (now.getMinutes() << 5) | (now.getSeconds() >> 1)) & 0xffff;
  const date = (((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate()) & 0xffff;
  for (const f of files) {
    const name = enc.encode(f.name);
    const data = typeof f.data === 'string' ? enc.encode(f.data) : f.data;
    const crc = crc32(data);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(6, 0x0800, true); // names are UTF-8
    local.setUint16(8, 0, true); // stored
    local.setUint16(10, time, true);
    local.setUint16(12, date, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, data.length, true);
    local.setUint32(22, data.length, true);
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);
    parts.push(new Uint8Array(local.buffer), name, data);
    const cd = new DataView(new ArrayBuffer(46));
    cd.setUint32(0, 0x02014b50, true);
    cd.setUint16(4, 20, true);
    cd.setUint16(6, 20, true);
    cd.setUint16(8, 0x0800, true);
    cd.setUint16(10, 0, true);
    cd.setUint16(12, time, true);
    cd.setUint16(14, date, true);
    cd.setUint32(16, crc, true);
    cd.setUint32(20, data.length, true);
    cd.setUint32(24, data.length, true);
    cd.setUint16(28, name.length, true);
    cd.setUint32(42, offset, true);
    central.push(new Uint8Array(cd.buffer), name);
    offset += 30 + name.length + data.length;
  }
  const cdSize = central.reduce((a, b) => a + b.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, cdSize, true);
  end.setUint32(16, offset, true);
  const all = [...parts, ...central, new Uint8Array(end.buffer)];
  const out = new Uint8Array(all.reduce((a, b) => a + b.length, 0));
  let p = 0;
  for (const a of all) { out.set(a, p); p += a.length; }
  return out;
}

export function unzip(bytes, inflateRaw) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  let eocd = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 22 - 65535); i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error("This isn't an Excel file (or it's damaged)");
  const count = dv.getUint16(eocd + 10, true);
  let p = dv.getUint32(eocd + 16, true);
  const files = {};
  for (let i = 0; i < count; i++) {
    if (p + 46 > b.length || dv.getUint32(p, true) !== 0x02014b50) throw new Error('The file is damaged');
    const method = dv.getUint16(p + 10, true);
    const csize = dv.getUint32(p + 20, true);
    const nlen = dv.getUint16(p + 28, true);
    const xlen = dv.getUint16(p + 30, true);
    const clen = dv.getUint16(p + 32, true);
    const loff = dv.getUint32(p + 42, true);
    const name = dec.decode(b.subarray(p + 46, p + 46 + nlen));
    p += 46 + nlen + xlen + clen;
    if (dv.getUint32(loff, true) !== 0x04034b50) throw new Error('The file is damaged');
    const start = loff + 30 + dv.getUint16(loff + 26, true) + dv.getUint16(loff + 28, true);
    const raw = b.subarray(start, start + csize);
    files[name] = { method, raw };
  }
  return {
    names: Object.keys(files),
    read(name) {
      const f = files[name];
      if (!f) return null;
      if (f.method === 0) return f.raw;
      if (f.method === 8) {
        if (!inflateRaw) throw new Error('Reading compressed files needs a decompressor');
        return new Uint8Array(inflateRaw(f.raw));
      }
      throw new Error('Unsupported compression in the file');
    },
    text(name) {
      const d = this.read(name);
      return d ? dec.decode(d) : null;
    },
  };
}

/* ── XML helpers ──────────────────────────────────────── */

export const esc = (s) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  // control characters aren't allowed in XML
  // eslint-disable-next-line no-control-regex
  .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '');

export function unesc(s) {
  return String(s)
    .replace(/_x([0-9a-fA-F]{4})_/g, (_, h) => String.fromCharCode(parseInt(h, 16)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(Number(d)))
    .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&');
}

function attrs(s) {
  const out = {};
  const re = /([\w:.-]+)\s*=\s*("([^"]*)"|'([^']*)')/g;
  let m;
  while ((m = re.exec(s))) out[m[1].replace(/^\w+:/, (x) => (x === 'r:' ? 'r:' : ''))] = unesc(m[3] ?? m[4]);
  return out;
}

export const colName = (i) => {
  let s = '';
  let n = i + 1;
  while (n > 0) { const r = (n - 1) % 26; s = String.fromCharCode(65 + r) + s; n = Math.floor((n - 1) / 26); }
  return s;
};
export const colIndex = (letters) => letters.toUpperCase().split('').reduce((a, c) => a * 26 + (c.charCodeAt(0) - 64), 0) - 1;
export const ref = (r, c) => `${colName(c)}${r + 1}`;

/* ── Writing .xlsx ────────────────────────────────────── */

// Cell styles available when writing (index in cellXfs)
const STYLES = ['default', 'header', 'money', 'moneyMuted', 'moneyInput', 'text', 'textInput', 'title', 'muted', 'pct', 'int', 'bold', 'moneyBold', 'monthKey', 'wrap', 'intInput',
  'dateA', 'dateB', 'textA', 'textB', 'moneyA', 'moneyB'];
const S = Object.fromEntries(STYLES.map((s, i) => [s, i]));

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="3"><numFmt numFmtId="164" formatCode="#,##0.00\\ &quot;€&quot;;\\-#,##0.00\\ &quot;€&quot;"/><numFmt numFmtId="165" formatCode="0.0%"/><numFmt numFmtId="166" formatCode="dd/mm/yyyy"/></numFmts>
<fonts count="5">
<font><sz val="11"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font>
<font><sz val="11"/><color rgb="FF7F7F7F"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="14"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><name val="Calibri"/><family val="2"/></font>
</fonts>
<fills count="7">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FF1F2933"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFFFF7D6"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFF2F2F2"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFFFFFFF"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFEAF1FB"/><bgColor indexed="64"/></patternFill></fill>
</fills>
<borders count="2">
<border><left/><right/><top/><bottom/><diagonal/></border>
<border><left style="thin"><color rgb="FFD9D9D9"/></left><right style="thin"><color rgb="FFD9D9D9"/></right><top style="thin"><color rgb="FFD9D9D9"/></top><bottom style="thin"><color rgb="FFD9D9D9"/></bottom><diagonal/></border>
</borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="${STYLES.length}">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>
<xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/>
<xf numFmtId="164" fontId="2" fillId="4" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="164" fontId="0" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyProtection="1"><protection locked="0"/></xf>
<xf numFmtId="49" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/>
<xf numFmtId="49" fontId="0" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyProtection="1" applyAlignment="1"><alignment wrapText="1"/><protection locked="0"/></xf>
<xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment wrapText="1" vertical="top"/></xf>
<xf numFmtId="165" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/>
<xf numFmtId="1" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/>
<xf numFmtId="0" fontId="4" fillId="0" borderId="0" xfId="0" applyFont="1"/>
<xf numFmtId="164" fontId="4" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyBorder="1"/>
<xf numFmtId="49" fontId="4" fillId="4" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment wrapText="1" vertical="top"/></xf>
<xf numFmtId="1" fontId="0" fillId="3" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyProtection="1"><protection locked="0"/></xf>
<xf numFmtId="166" fontId="0" fillId="5" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left"/></xf>
<xf numFmtId="166" fontId="0" fillId="6" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left"/></xf>
<xf numFmtId="49" fontId="0" fillId="5" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="49" fontId="0" fillId="6" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="164" fontId="0" fillId="5" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1"/>
<xf numFmtId="164" fontId="0" fillId="6" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1"/>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

function cellXml(r, c, cell) {
  if (cell === null || cell === undefined || cell === '') return '';
  const o = typeof cell === 'object' && !(cell instanceof Date) ? cell : { v: cell };
  const s = o.style ? S[o.style] ?? 0 : 0;
  const sa = s ? ` s="${s}"` : '';
  const at = ref(r, c);
  if (o.f) return `<c r="${at}"${sa}><f>${esc(o.f)}</f>${typeof o.v === 'number' ? `<v>${o.v}</v>` : ''}</c>`;
  if (o.v === null || o.v === undefined || o.v === '') return s ? `<c r="${at}"${sa}/>` : '';
  if (typeof o.v === 'number') return Number.isFinite(o.v) ? `<c r="${at}"${sa}><v>${o.v}</v></c>` : '';
  if (typeof o.v === 'boolean') return `<c r="${at}"${sa} t="b"><v>${o.v ? 1 : 0}</v></c>`;
  return `<c r="${at}"${sa} t="inlineStr"><is><t xml:space="preserve">${esc(o.v)}</t></is></c>`;
}

function sheetXml(sheet) {
  const rows = sheet.rows || [];
  const cols = sheet.columns || [];
  const colsXml = cols.length ? `<cols>${cols.map((c, i) => `<col min="${i + 1}" max="${i + 1}" width="${c.width || 12}" customWidth="1"${c.style ? ` style="${S[c.style] || 0}"` : ''}/>`).join('')}</cols>` : '';
  const freeze = sheet.freeze;
  const pane = freeze && (freeze.row || freeze.col)
    ? `<pane${freeze.col ? ` xSplit="${freeze.col}"` : ''}${freeze.row ? ` ySplit="${freeze.row}"` : ''} topLeftCell="${ref(freeze.row || 0, freeze.col || 0)}" activePane="bottomRight" state="frozen"/>`
    : '';
  const rowsXml = rows.map((row, r) => {
    const cells = (row || []).map((cell, c) => cellXml(r, c, cell)).join('');
    const ht = sheet.rowHeights?.[r];
    return cells || ht ? `<row r="${r + 1}"${ht ? ` ht="${ht}" customHeight="1"` : ''}>${cells}</row>` : '';
  }).join('');
  const protect = sheet.protect ? '<sheetProtection sheet="1" objects="1" scenarios="1" formatCells="0" formatColumns="0" formatRows="0" sort="0" autoFilter="0"/>' : '';
  const dv = (sheet.validations || []).length
    ? `<dataValidations count="${sheet.validations.length}">${sheet.validations.map((v) => `<dataValidation type="${v.type}"${v.type === 'list' ? '' : ` operator="${v.operator || 'between'}"`} allowBlank="1" showErrorMessage="1"${v.prompt ? ` showInputMessage="1" prompt="${esc(v.prompt)}"` : ''} errorTitle="${esc(v.errorTitle || 'Check the value')}" error="${esc(v.error || '')}" sqref="${v.sqref}"><formula1>${esc(v.formula1)}</formula1>${v.formula2 !== undefined ? `<formula2>${esc(v.formula2)}</formula2>` : ''}</dataValidation>`).join('')}</dataValidations>`
    : '';
  const merges = (sheet.merges || []).length ? `<mergeCells count="${sheet.merges.length}">${sheet.merges.map((m) => `<mergeCell ref="${m}"/>`).join('')}</mergeCells>` : '';
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheetViews><sheetView workbookViewId="0"${sheet.tabColor ? '' : ''}>${pane}</sheetView></sheetViews><sheetFormatPr defaultRowHeight="15"/>${colsXml}<sheetData>${rowsXml}</sheetData>${protect}${merges}${dv}${sheet.table ? '<tableParts count="1"><tablePart r:id="rId1"/></tableParts>' : ''}</worksheet>`;
}

// sheets: [{ name, rows: [[cell]], columns: [{ width }], freeze: { row, col }, hidden, protect, validations, merges }]
// cell: string | number | boolean | null | { v, style, f }
export function writeXlsx(sheets) {
  const safe = sheets.map((s, i) => ({ ...s, name: sheetName(s.name, i, sheets) }));
  const wb = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><bookViews><workbookView activeTab="0"/></bookViews><sheets>${safe.map((s, i) => `<sheet name="${esc(s.name)}" sheetId="${i + 1}"${s.hidden ? ' state="hidden"' : ''} r:id="rId${i + 1}"/>`).join('')}</sheets></workbook>`;
  const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${safe.map((s, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')}<Relationship Id="rId${safe.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`;
  const types = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${safe.map((s, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')}</Types>`;
  const root = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`;
  const tables = [];
  safe.forEach((sh, i) => { if (sh.table) tables.push({ sheet: i + 1, n: tables.length + 1, ...sh.table }); });
  const tableFiles = tables.flatMap((t) => [
    { name: `xl/tables/table${t.n}.xml`, data: tableXml(t) },
    { name: `xl/worksheets/_rels/sheet${t.sheet}.xml.rels`, data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/table" Target="../tables/table${t.n}.xml"/></Relationships>` },
  ]);
  const typesWithTables = types.replace('</Types>', `${tables.map((t) => `<Override PartName="/xl/tables/table${t.n}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.table+xml"/>`).join('')}</Types>`);
  return zip([
    { name: '[Content_Types].xml', data: typesWithTables },
    { name: '_rels/.rels', data: root },
    { name: 'xl/workbook.xml', data: wb },
    { name: 'xl/_rels/workbook.xml.rels', data: rels },
    { name: 'xl/styles.xml', data: STYLES_XML },
    ...safe.map((s, i) => ({ name: `xl/worksheets/sheet${i + 1}.xml`, data: sheetXml(s) })),
    ...tableFiles,
  ]);
}

// An Excel table (Insert → Table): header with filter buttons and banded rows. ref covers the header and the rows.
function tableXml(t) {
  const name = String(t.name || `Table${t.n}`).replace(/[^A-Za-z0-9_]/g, '_').replace(/^([^A-Za-z_])/, '_$1');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<table xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" id="${t.n}" name="${esc(name)}" displayName="${esc(name)}" ref="${t.ref}" totalsRowShown="0"><autoFilter ref="${t.ref}"/><tableColumns count="${t.columns.length}">${t.columns.map((c, i) => `<tableColumn id="${i + 1}" name="${esc(c)}"/>`).join('')}</tableColumns><tableStyleInfo name="${esc(t.style || 'TableStyleMedium2')}" showFirstColumn="0" showLastColumn="0" showRowStripes="1" showColumnStripes="0"/></table>`;
}

// Days since 1899-12-30, as Excel stores dates
export const dateToSerial = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(1899, 11, 30)) / 864e5);
};

// Excel sheet names: max 31 chars, no []:*?/\ and unique
function sheetName(name, i, all) {
  let n = String(name || `Sheet${i + 1}`).replace(/[[\]:*?/\\]/g, ' ').trim().slice(0, 31) || `Sheet${i + 1}`;
  const earlier = all.slice(0, i).map((s, j) => String(s.name || `Sheet${j + 1}`).replace(/[[\]:*?/\\]/g, ' ').trim().slice(0, 31));
  let k = 2;
  const base = n;
  while (earlier.includes(n)) n = `${base.slice(0, 28)} ${k++}`;
  return n;
}

/* ── Reading .xlsx ────────────────────────────────────── */

const BUILTIN_DATE_FORMATS = new Set([14, 15, 16, 17, 18, 19, 20, 21, 22, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 45, 46, 47, 50, 51, 52, 53, 54, 55, 56, 57, 58]);
function isDateFormat(code) {
  if (!code) return false;
  const c = code.replace(/"[^"]*"/g, '').replace(/\[[^\]]*\]/g, '').replace(/\\./g, '');
  return /[dmyhs]/i.test(c) && !/^[#0.,%\s]*$/.test(c);
}

export function serialToDate(serial, date1904 = false) {
  const ms = Math.round((serial + (date1904 ? 1462 : 0)) * 864e5) + Date.UTC(1899, 11, 30);
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

function textOf(xml) {
  // Shared/inline string: all <t> runs, skipping phonetic hints
  const clean = xml.replace(/<rPh\b[\s\S]*?<\/rPh>/g, '');
  let out = '';
  const re = /<t\b[^>]*>([\s\S]*?)<\/t>|<t\b[^>]*\/>/g;
  let m;
  while ((m = re.exec(clean))) out += m[1] ? unesc(m[1]) : '';
  return out;
}

export function readXlsx(bytes, { inflateRaw } = {}) {
  const z = unzip(bytes, inflateRaw);
  const wbXml = z.text('xl/workbook.xml');
  if (!wbXml) throw new Error("This doesn't look like an Excel workbook");
  const date1904 = /date1904\s*=\s*"(1|true)"/i.test(wbXml);
  const relsXml = z.text('xl/_rels/workbook.xml.rels') || '';
  const rels = {};
  for (const m of relsXml.matchAll(/<Relationship\b([^>]*)\/?>/g)) {
    const a = attrs(m[1]);
    rels[a.Id] = a.Target;
  }
  const shared = [];
  const ssXml = z.text('xl/sharedStrings.xml');
  if (ssXml) for (const m of ssXml.matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>|<si\b[^>]*\/>/g)) shared.push(m[1] ? textOf(m[1]) : '');
  const formats = {};
  const xfFormat = [];
  const stXml = z.text('xl/styles.xml');
  if (stXml) {
    for (const m of stXml.matchAll(/<numFmt\b([^>]*)\/?>/g)) { const a = attrs(m[1]); formats[Number(a.numFmtId)] = a.formatCode; }
    const xfs = stXml.match(/<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/);
    if (xfs) for (const m of xfs[1].matchAll(/<xf\b([^>]*?)(\/>|>)/g)) xfFormat.push(Number(attrs(m[1]).numFmtId || 0));
  }
  const isDateStyle = (s) => {
    const id = xfFormat[s];
    if (id === undefined) return false;
    return BUILTIN_DATE_FORMATS.has(id) || isDateFormat(formats[id]);
  };

  const sheets = [];
  for (const m of wbXml.matchAll(/<sheet\b([^>]*?)\/?>/g)) {
    const a = attrs(m[1]);
    const rid = a['r:id'] || a.id;
    let target = rels[rid] || '';
    target = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
    const xml = z.text(target);
    if (!xml) continue;
    sheets.push({ name: a.name || `Sheet${sheets.length + 1}`, hidden: a.state === 'hidden' || a.state === 'veryHidden', rows: readSheet(xml, shared, isDateStyle, date1904) });
  }
  if (!sheets.length) throw new Error('The workbook has no sheets');
  return { sheets };
}

function readSheet(xml, shared, isDateStyle, date1904) {
  const rows = [];
  const data = xml.match(/<sheetData\b[^>]*>([\s\S]*?)<\/sheetData>/);
  if (!data) return rows;
  let nextRow = 0;
  for (const rm of data[1].matchAll(/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g)) {
    const ra = attrs(rm[1]);
    const r = ra.r ? Number(ra.r) - 1 : nextRow;
    nextRow = r + 1;
    if (r > 100000) break;
    const row = [];
    let nextCol = 0;
    for (const cm of (rm[2] || '').matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const ca = attrs(cm[1]);
      let c = nextCol;
      if (ca.r) { const mm = ca.r.match(/^([A-Z]+)/i); if (mm) c = colIndex(mm[1]); }
      nextCol = c + 1;
      if (c > 1000) continue;
      const inner = cm[2] || '';
      const vm = inner.match(/<v\b[^>]*>([\s\S]*?)<\/v>/);
      const raw = vm ? unesc(vm[1]) : null;
      let cell = null;
      const t = ca.t || 'n';
      if (t === 's' && raw !== null) cell = { v: shared[Number(raw)] ?? '', t: 's' };
      else if (t === 'inlineStr') cell = { v: textOf(inner.match(/<is\b[^>]*>([\s\S]*?)<\/is>/)?.[1] || ''), t: 's' };
      else if (t === 'str' && raw !== null) cell = { v: raw, t: 's' };
      else if (t === 'b' && raw !== null) cell = { v: raw === '1' || raw === 'true', t: 'b' };
      else if (t === 'e') cell = { v: raw || '#ERROR', t: 'e' };
      else if (t === 'd' && raw !== null) cell = { v: raw.slice(0, 10), t: 'd' };
      else if (raw !== null && raw !== '') {
        const n = Number(raw);
        if (Number.isFinite(n)) cell = ca.s && isDateStyle(Number(ca.s)) ? { v: serialToDate(n, date1904), t: 'd', serial: n } : { v: n, t: 'n' };
        else cell = { v: raw, t: 's' };
      }
      if (cell !== null && !(cell.t === 's' && cell.v === '')) row[c] = cell;
    }
    rows[r] = row;
  }
  for (let i = 0; i < rows.length; i++) if (!rows[i]) rows[i] = [];
  return rows;
}

/* ── CSV ──────────────────────────────────────────────── */

export function readCsv(text) {
  const s = String(text).replace(/^﻿/, '');
  const firstLine = s.split(/\r?\n/, 1)[0] || '';
  const count = (ch) => { let n = 0; let q = false; for (const c of firstLine) { if (c === '"') q = !q; else if (c === ch && !q) n++; } return n; };
  const delim = [';', ',', '\t'].reduce((best, d) => (count(d) > count(best) ? d : best), ',');
  const rows = [];
  let row = [];
  let field = '';
  let q = false;
  let quoted = false;
  const push = () => { row.push(field === '' && !quoted ? null : { v: field, t: 's' }); field = ''; quoted = false; };
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (q) {
      if (c === '"') { if (s[i + 1] === '"') { field += '"'; i++; } else q = false; } else field += c;
    } else if (c === '"') { q = true; quoted = true; } else if (c === delim) push();
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && s[i + 1] === '\n') i++;
      push(); rows.push(row); row = [];
    } else field += c;
  }
  if (field !== '' || row.length || quoted) { push(); rows.push(row); }
  return { sheets: [{ name: 'CSV', hidden: false, rows: rows.map((r) => (r.every((x) => x === null) ? [] : r)) }] };
}

// Either format, from the bytes
export function readSpreadsheet(bytes, opts) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (b[0] === 0x50 && b[1] === 0x4b) return readXlsx(b, opts);
  if (b[0] === 0xd0 && b[1] === 0xcf) throw new Error('This is an old Excel file (.xls). Save it as .xlsx and try again.');
  const text = dec.decode(b);
  if (/^\s*</.test(text)) throw new Error("That's a web page, not a spreadsheet. If it's a Google Sheet, check it's shared or published.");
  return readCsv(text);
}

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
